/**
 * Media Gallery (§6 "Galéria") interactions.
 *
 * The b-media-gallery block (resources/blocks/b-media-gallery/view.blade.php)
 * renders, per gallery:
 *   - a horizontally-scrollable masonry collage scroller
 *       `[data-gallery-scroller]` (also `.lightgallery-gallery`, id=<uid>)
 *   - desktop prev/next arrows `[data-gallery-prev] / [data-gallery-next]`
 *   - a mobile "Zobraziť viac" button `[data-gallery-more]`
 * each control carrying `data-gallery-target="<uid>"` pointing at its scroller.
 *
 * This binds:
 *   - the arrows → smooth `scrollBy` on the matching scroller (desktop collage).
 *   - "Zobraziť viac" → opens the full lightbox by clicking the scroller's first
 *     `<a>` tile. lightGallery (wired in app.ts via
 *     `new Lightbox('.lightgallery-gallery', { selector: undefined })`) binds a
 *     delegated click handler to every `<a>` inside the container, so clicking
 *     the first link opens the gallery at index 0 with ALL tiles as slides —
 *     including the mobile-hidden 7th+ tiles, which remain in the DOM.
 */
class Gallery {
  constructor() {
    this.init();
  }

  private scrollerFor(target: string | null): HTMLElement | null {
    if (!target) return null;
    const el = document.getElementById(target);
    return el && el.matches('[data-gallery-scroller]') ? el : null;
  }

  private scrollAmount(scroller: HTMLElement): number {
    // Scroll by roughly one viewport of the collage, leaving a small overlap.
    return Math.max(scroller.clientWidth * 0.9, 320);
  }

  private init(): void {
    document.addEventListener('click', (event) => {
      const trigger = (event.target as HTMLElement)?.closest<HTMLElement>(
        '[data-gallery-prev], [data-gallery-next], [data-gallery-more]',
      );
      if (!trigger) return;

      const scroller = this.scrollerFor(
        trigger.getAttribute('data-gallery-target'),
      );
      if (!scroller) return;

      if (trigger.hasAttribute('data-gallery-prev')) {
        scroller.scrollBy({
          left: -this.scrollAmount(scroller),
          behavior: 'smooth',
        });
        return;
      }

      if (trigger.hasAttribute('data-gallery-next')) {
        scroller.scrollBy({
          left: this.scrollAmount(scroller),
          behavior: 'smooth',
        });
        return;
      }

      if (trigger.hasAttribute('data-gallery-more')) {
        // Open the full lightbox: click the first tile link, which lightGallery
        // has wired to open the gallery at index 0.
        const firstLink = scroller.querySelector<HTMLAnchorElement>('a');
        if (firstLink) {
          event.preventDefault();
          firstLink.click();
        }
      }
    });
  }
}

export default Gallery;
